import React, { useEffect, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { makeStyles } from '~/tools/makeStyles';
import services from '../api/services';
import LoadingPage from './LoadingPage';
import { BrandedCardLayout } from '../layouts/BrandedCardLayout';
import { StatusAlert } from '../components/StatusAlert';
import { ERROR_ROUTE } from '../constants/routes';
import { useRouter } from '../hooks/useRouter';
import { fetchSurveyFail } from '../redux/actions';
import { useAppDispatch } from '../redux/ReduxProvider';
import OpenSurveyFlow, { RawSurveyElement } from './OpenSurveyFlow';

const ACCESS_CODE_PARAM = 'c';

const ACCESSIBLE = 'ACCESSIBLE';
const UNAVAILABLE = 'UNAVAILABLE';
const ACCESS_CODE_REQUIRED = 'ACCESS_CODE_REQUIRED';
const ACCESS_CODE_INCORRECT = 'ACCESS_CODE_INCORRECT';

type OpenSurvey = {
  id: number;
  name: string;
  questions: RawSurveyElement[];
};

// Mirrors OpenSurveyAccess in the API's survey schema.
type OpenSurveyAccess =
  | { status: typeof ACCESSIBLE; survey: OpenSurvey }
  | {
      status: typeof UNAVAILABLE | typeof ACCESS_CODE_REQUIRED | typeof ACCESS_CODE_INCORRECT;
      survey: null;
    };

const useStyles = makeStyles((theme) => ({
  heading: {
    fontSize: 18,
    fontWeight: 400,
    textAlign: 'center',
    color: theme.palette.text.primary,
    margin: 0,
    marginBottom: 24,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  alert: {
    marginTop: 8,
    marginBottom: 12,
  },
  field: {
    marginBottom: 24,
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 40,
  },
  loadingText: {
    fontSize: 16,
    color: theme.palette.text.primary,
    marginTop: 16,
  },
}));

type OpenSurveyAccessState = {
  access: OpenSurveyAccess | null;
  hasFailed: boolean;
};

const useOpenSurveyAccess = (openSurveyExternalId: string | undefined, accessCode: string): OpenSurveyAccessState => {
  const [access, setAccess] = useState<OpenSurveyAccess | null>(null);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    let isCurrent = true;

    setAccess(null);
    setHasFailed(false);

    services
      .fetchOpenSurvey(openSurveyExternalId, accessCode)
      .then((result: OpenSurveyAccess) => {
        if (isCurrent) setAccess(result);
      })
      .catch(() => {
        if (isCurrent) setHasFailed(true);
      });

    // Without this, a stale response for the previous code could overwrite the new one.
    return () => {
      isCurrent = false;
    };
  }, [openSurveyExternalId, accessCode]);

  return { access, hasFailed };
};

const OpenSurveyPage = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const { open_survey_id: openSurveyExternalId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [enteredCode, setEnteredCode] = useState('');
  const [hasSubmittedCode, setHasSubmittedCode] = useState(false);
  const { goTo } = useRouter();
  const dispatch = useAppDispatch();

  const accessCode = searchParams.get(ACCESS_CODE_PARAM)?.trim() ?? '';
  const { access, hasFailed } = useOpenSurveyAccess(openSurveyExternalId, accessCode);

  // Every failure looks the same on purpose, so the page never says which one it was.
  const status = hasFailed ? UNAVAILABLE : access?.status;
  const isAskingForCode = status === ACCESS_CODE_REQUIRED || status === ACCESS_CODE_INCORRECT;
  const isUnusable = status !== undefined && status !== ACCESSIBLE && !isAskingForCode;
  const isCheckingCode = hasSubmittedCode && access === null && !hasFailed;

  useEffect(() => {
    if (!isUnusable) return;
    dispatch(fetchSurveyFail(t('survey.open-survey.error-not-available')));
    goTo(ERROR_ROUTE);
  }, [isUnusable, goTo, dispatch, t]);

  useEffect(() => {
    if (status === ACCESS_CODE_INCORRECT) setEnteredCode('');
  }, [status]);

  const onCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEnteredCode(event.target.value);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setHasSubmittedCode(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(ACCESS_CODE_PARAM, enteredCode.trim());
    setSearchParams(nextParams, { replace: true });
  };

  if (isAskingForCode || isCheckingCode) {
    const passcodeLabel = `${t('survey.open-survey.passcode-label')} (${t('required')})`;

    return (
      <BrandedCardLayout>
        <Typography variant="h1" className={styles.heading}>
          {t('survey.open-survey.passcode-heading')}
        </Typography>

        <form className={styles.form} onSubmit={onSubmit}>
          {status === ACCESS_CODE_INCORRECT && (
            <StatusAlert severity="error" className={styles.alert}>
              {t('survey.open-survey.passcode-incorrect')}
            </StatusAlert>
          )}
          <TextField
            required
            fullWidth
            autoFocus
            variant="outlined"
            className={styles.field}
            label={passcodeLabel}
            value={enteredCode}
            onChange={onCodeChange}
            disabled={isCheckingCode}
          />
          <Button
            type="submit"
            variant="contained"
            size="small"
            fullWidth
            disabled={!enteredCode.trim() || isCheckingCode}
          >
            {t('survey.submit')}
          </Button>
        </form>

        {isCheckingCode && (
          <div className={styles.loading}>
            <CircularProgress />
            <Typography className={styles.loadingText}>{t('survey.open-survey.loading')}</Typography>
          </div>
        )}
      </BrandedCardLayout>
    );
  }

  if (access === null || access.status !== ACCESSIBLE) return <LoadingPage />;

  return (
    <OpenSurveyFlow
      openSurveyExternalId={openSurveyExternalId ?? ''}
      openSurveyAccessCode={accessCode}
      survey={access.survey}
    />
  );
};

export default OpenSurveyPage;
